import React from 'react'
import { Notification } from '../../../Interfaces/Types'
import { sevMeta } from '../../../Utilities/notificationSeverity'

const FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

// Times are stored as UTC ISO-8601; show them in the viewer's local time,
// clean and locale-stable (month name, no seconds/AM-PM ambiguity).
const formatDateTime = (value?: string): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const LANG_NAMES: Record<string, string> = { nl: 'Nederlands', en: 'English' }

// nl first (primary), en next, then any other languages added via JSON.
const orderLanguages = (content: Record<string, { title?: string; main?: string }>): string[] => {
  const keys = Object.keys(content || {})
  const preferred = ['nl', 'en'].filter((k) => keys.includes(k))
  return [...preferred, ...keys.filter((k) => !preferred.includes(k)).sort()]
}

interface NotificationItemProps {
  notification: Notification
}

// Read-only detail view of a notification (rendered inside the details dialog).
const NotificationItem: React.FC<NotificationItemProps> = ({ notification }) => {
  const content = (notification?.content ?? {}) as unknown as Record<string, { title?: string; main?: string }>
  const sev = sevMeta(notification?.severity)
  const title = content.nl?.title || content.en?.title || Object.values(content)[0]?.title || '(no title)'

  return (
    <div style={{ fontFamily: FONT, color: 'var(--tma-text-strong)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <span
          style={{
            fontFamily: FONT,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: sev.color,
            background: sev.bg,
            padding: '3px 9px',
            borderRadius: '999px'
          }}
        >
          {sev.label}
        </span>
        <span style={{ fontSize: '17px', fontWeight: 700 }}>{title}</span>
      </div>
      <div style={{ color: 'var(--tma-text-muted)', fontSize: '13px', margin: '2px 0 16px' }}>
        {formatDateTime(notification?.starttime)} → {formatDateTime(notification?.endtime)} · your local time
      </div>

      {orderLanguages(content).map((lang) => (
        <div key={lang} style={{ borderTop: '1px solid var(--tma-card-border)', padding: '12px 0' }}>
          <span
            style={{
              display: 'inline-block',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--tma-text-muted)',
              border: '1px solid var(--tma-list-card-border)',
              borderRadius: '5px',
              padding: '1px 6px',
              marginBottom: '6px'
            }}
          >
            {LANG_NAMES[lang] || lang.toUpperCase()}
          </span>
          <p style={{ margin: '0 0 3px', fontWeight: 600, fontSize: '14px' }}>{content[lang]?.title || '-'}</p>
          <p style={{ margin: 0, color: 'var(--tma-text-muted)', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
            {content[lang]?.main || '-'}
          </p>
        </div>
      ))}
    </div>
  )
}

export default NotificationItem
